import { Warning } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect } from "react";

import { FileAttachments } from "features/attachments";
import TemplateAttachment from "features/templates/components/TemplateAttachment";
import useTemplatePreview from "features/templates/datahooks/useTemplatePreview";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import { areTemplatesEqual } from "features/templates/utils";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { HTTPError } from "common/helpers/HTTP";

interface PreviewContentProps {
  updatedTemplate: Exclude<TemplateSchemaType, VoiceTemplateSchemaType>;
  previewTemplate:
    | Exclude<TemplateSchemaType, VoiceTemplateSchemaType>
    | undefined;
  targetProfileId: string;
  senderProfileId: string;
  onUpdatePreview: () => void;
  campaignData?: {
    campaignId: string;
    isTranslateEnabled: boolean;
    sourceLanguageCode: string;
  };
  shouldShowTranslatedText: boolean;
}

const defaultMessage = "Your preview will be shown here...";

export default function PreviewContent({
  updatedTemplate,
  previewTemplate,
  targetProfileId,
  senderProfileId,
  onUpdatePreview,
  campaignData,
  shouldShowTranslatedText,
}: PreviewContentProps) {
  const {
    injectContent,
    translateContent,
    refetchPreview,
    previewError,
    isLoadingPreview,
  } = useTemplatePreview({
    previewData: {
      template: previewTemplate,
      targetProfileId,
      senderProfileId,
      campaignData,
    },
  });

  const isEmptyTemplate =
    !updatedTemplate.message ||
    (updatedTemplate.type === "INMAIL_TEMPLATE" && !updatedTemplate.subject) ||
    Object.values(updatedTemplate.ai_descriptors).some(
      (variableValue) => !variableValue,
    );

  const isTemplateUnchanged =
    previewTemplate &&
    updatedTemplate &&
    areTemplatesEqual(previewTemplate, updatedTemplate);

  useEffect(() => {
    if (!isTemplateUnchanged && !isEmptyTemplate) {
      onUpdatePreview();
    }
  }, [targetProfileId]);

  function onRefetch() {
    if (previewError) {
      refetchPreview();
    }
    if (!isTemplateUnchanged) {
      onUpdatePreview();
    }
  }

  let message = "";
  let subject = "";

  if (
    campaignData?.isTranslateEnabled &&
    translateContent &&
    shouldShowTranslatedText
  ) {
    message = translateContent.message;
    subject = translateContent.subject;
  } else if (injectContent) {
    message = injectContent.message;
    subject = injectContent.subject;
  }

  let subjectContent = <span className="text-sm">{subject}</span>;
  let messageContent = (
    <p
      className={clsx(
        "whitespace-pre-wrap break-words text-sm",
        !message && "text-black-500",
      )}
    >
      {message || defaultMessage}
    </p>
  );

  if (previewError) {
    const errorMessage =
      previewError instanceof HTTPError &&
      previewError.data === "VariablesMissingError"
        ? "Cannot show preview, or send message: Missing variable. Make sure all variables have fallbacks/prompts"
        : "Error loading preview";
    messageContent = (
      <div className="flex items-center gap-1">
        <Warning size={16} weight="fill" className="fill-red-500" />
        <p className="text-xs text-red-500">{errorMessage}</p>
      </div>
    );
  } else if (isLoadingPreview) {
    messageContent = (
      <div className="flex flex-col gap-1">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-8/12" />
      </div>
    );
    subjectContent = <Skeleton className="h-5 w-6/12" />;
  }
  return (
    <>
      {subject && (
        <div className="mx-2 flex flex-col border-b border-b-black-200 px-1 py-2">
          <span className="text-button-xxs text-black-500">Subject</span>
          {subjectContent}
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-y-auto p-3 scrollbar-thin">
        {messageContent}
        {previewTemplate && "attachments" in previewTemplate && (
          <FileAttachments
            attachments={previewTemplate.attachments}
            renderAttachment={(attachment) => (
              <TemplateAttachment
                key={attachment.id}
                attachment={{
                  id: attachment.id,
                  name: attachment.file_name,
                  byteSize: attachment.file_metadata.size,
                  mimeType: attachment.file_metadata.mimetype,
                }}
              />
            )}
          />
        )}
      </div>
      {!isLoadingPreview &&
        (!isTemplateUnchanged || previewError) &&
        !isEmptyTemplate && (
          <Button
            onClick={onRefetch}
            variant="outline-black"
            className="absolute inset-x-0 bottom-3 mx-auto w-fit self-center"
            size="sm"
          >
            {previewError ? "Try again" : "Update changes"}
          </Button>
        )}
    </>
  );
}
